import { onValue, runTransaction } from 'firebase/database';
import { auth } from '../firebase';

const pending = new Set();
const READ_TIMEOUT = 15000;
const WRITE_TIMEOUT = 45000;
const MAX_STRING_BYTES = 10 * 1024 * 1024;

const utf8Bytes = text => {
  let bytes = 0;
  for (const character of text) {
    const code = character.codePointAt(0);
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
};

// Firebase validates updater results outside its callback error handling on retries.
// Validate here so malformed records cannot strand a transaction in the SDK queue.
export function validateTransactionValue(value, ancestors = new Set(), depth = 0, pathBytes = 1) {
  if (depth > 32 || pathBytes > 768) throw new Error('Data exceeds the database path limits. No changes were saved.');
  if (value === null || typeof value === 'boolean') return;
  if (typeof value === 'string') {
    if (value.length > MAX_STRING_BYTES / 3 && utf8Bytes(value) > MAX_STRING_BYTES) throw new Error('Text is too large. No changes were saved.');
    return;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return;
  if (!value || typeof value !== 'object' || ancestors.has(value)) throw new Error('Invalid data. No changes were saved.');
  if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) throw new Error('Invalid record. No changes were saved.');
  ancestors.add(value);
  Object.entries(value).forEach(([key, child]) => {
    if (!key || ['__proto__', 'constructor', 'prototype'].includes(key) || [...key].some(character => '.#$[]/'.includes(character) || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) throw new Error('Invalid record key. No changes were saved.');
    validateTransactionValue(child, ancestors, depth + 1, pathBytes + utf8Bytes(key) + (depth > 0 ? 1 : 0));
  });
  ancestors.delete(value);
}

function checkScope(before, after, paths) {
  if (paths.some(path => path.length === 0)) return;
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  for (const key of keys) {
    const permitted = paths.filter(path => path[0] === key).map(path => path.slice(1));
    if (permitted.length) checkScope(before?.[key], after?.[key], permitted);
    else if (JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key])) throw new Error('Blocked an update outside this action. No changes were saved.');
  }
}

const hasStoredValue = value => value !== null && (typeof value !== 'object' || Object.values(value).some(hasStoredValue));

// Never allow exceptions or null replacement values to escape into Firebase's
// retry queue. Keep a read subscription alive until the write actually settles.
export async function runSafeTransaction(target, change, { allowCreate = false, allowedPaths, deleteRecord = false, atomicAction } = {}) {
  const owner = auth.currentUser?.uid;
  if (!owner) throw new Error('Sign in before saving.');
  const key = target.toString().replace(/\/$/, '');
  const segments = key.replace(/^https?:\/\/[^/]+/, '').split('/').filter(Boolean).map(decodeURIComponent);
  if (atomicAction && (segments.length !== 3 || segments[0] !== 'scavengerData' || segments[2] !== 'fitnessData'
    || !['workout-history', 'warmup-history', 'physio-progress', 'skip-unfinished'].includes(atomicAction))) throw new Error('Invalid atomic action. No changes were saved.');
  const marker = atomicAction ? { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, type: atomicAction, createdAt: Date.now() } : null;
  if ([...pending].some(path => path === key || path.startsWith(`${key}/`) || key.startsWith(`${path}/`))) throw new Error('Another change is still awaiting confirmation. Reconnect and refresh before trying again.');
  pending.add(key);
  let unsubscribe;
  let timer;
  let ready = false;
  const cleanup = () => { clearTimeout(timer); unsubscribe?.(); pending.delete(key); };
  const operation = (async () => {
    await new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Error('Could not load current data. Check your connection and try again.')), READ_TIMEOUT);
      unsubscribe = onValue(target, () => { ready = true; clearTimeout(timer); resolve(); }, reject);
    });
    if (auth.currentUser?.uid !== owner) throw new Error('Your account changed. Please refresh.');
    let failure;
    const result = await runTransaction(target, current => {
      failure = undefined;
      try {
        if (auth.currentUser?.uid !== owner) return undefined;
        if (current == null && !allowCreate) return undefined;
        const before = allowedPaths ? JSON.parse(JSON.stringify(current)) : current;
        const next = change(current);
        if (next === undefined) return undefined;
        if (next === null && deleteRecord && segments.length >= 4) return null;
        if (!next || typeof next !== 'object' || Array.isArray(next)) throw new Error('Blocked an unsafe replacement. No changes were saved.');
        const output = marker ? { ...next, _atomicAction: marker } : next;
        validateTransactionValue(output, new Set(), segments.length, Math.max(1, segments.length) + segments.reduce((total, segment) => total + utf8Bytes(segment), 0));
        // Empty objects/arrays and all-null trees are also deletions in RTDB.
        if (!hasStoredValue(next)) throw new Error('Blocked an empty replacement. No changes were saved.');
        if (allowedPaths) checkScope(before, next, allowedPaths.map(path => path.split('/')));
        return output;
      } catch (error) {
        failure = error;
        return undefined;
      }
    }, { applyLocally: false });
    if (auth.currentUser?.uid !== owner) throw new Error('Your account changed. Please refresh.');
    if (failure) throw failure;
    return result;
  })();
  // A UI timeout is not cancellation: retain the lock and listener until the
  // underlying operation settles, so retrying cannot overlap an uncertain write.
  operation.then(cleanup, cleanup);
  return Promise.race([operation, new Promise((_, reject) => {
      const timeout = setTimeout(() => reject(new Error(ready
        ? 'Still awaiting server confirmation. Do not retry; reconnect and refresh to check the result.'
        : 'Could not load current data. Check your connection and try again.')), WRITE_TIMEOUT);
      operation.then(() => clearTimeout(timeout), () => clearTimeout(timeout));
  })]);
}
